import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

export interface EditOperationProps {
  title: string;
  formData: {
    title: string;
    agencyAssignedIdentifier: string;
    description: string;
    date: string;
    time: string;
    operationType: {
      planned: boolean;
      emergency: boolean;
    };
  };
  onFormDataChange: (updater: (prev: any) => any) => void;
  backgroundColor?: string;
}

export default function EditOperation({
  title,
  formData,
  onFormDataChange,
  backgroundColor
}: EditOperationProps) {
  const theme = useTheme();
  const formWidth = 800;
  const sectionWidth = 832;

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Typography variant="h5">
          {title}
        </Typography>
        <Typography variant="body1">
          Required fields are marked with an asterisk.
        </Typography>
      </Stack>

      <Box
        sx={{
          p: 2,
          backgroundColor: backgroundColor ?? theme.palette.blueGrey[50],
          borderRadius: '10px',
          width: '100%',
          maxWidth: sectionWidth,
        }}
        data-testid="operation-details"
      >
        <Stack spacing={2}>
          <TextField
            label="Title"
            value={formData.title}
            onChange={(e) => onFormDataChange(prev => ({ ...prev, title: e.target.value }))}
            variant="outlined"
            size="medium"
            required
            fullWidth
            sx={{ maxWidth: formWidth }}
          />
          <TextField
            label="Agency Assigned Identifier"
            value={formData.agencyAssignedIdentifier}
            onChange={(e) => onFormDataChange(prev => ({ ...prev, agencyAssignedIdentifier: e.target.value }))}
            variant="outlined"
            size="medium"
            required
            fullWidth
            sx={{ maxWidth: formWidth }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => onFormDataChange(prev => ({ ...prev, description: e.target.value }))}
            variant="outlined"
            multiline
            maxRows={3}
            fullWidth
            sx={{
              maxWidth: formWidth,
              '& .MuiInputBase-input': {
                padding: '0',
              },
            }}
          />
          <Stack direction="row" spacing={1} sx={{ maxWidth: formWidth }}>
            <TextField
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, date: e.target.value }))}
              variant="outlined"
              required
              fullWidth
              sx={{ flex: 1 }}
            />
            <TextField
              label="Time"
              type="time"
              value={formData.time}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, time: e.target.value }))}
              variant="outlined"
              fullWidth
              sx={{ flex: 1 }}
            />
          </Stack>
          <FormGroup sx={{ maxWidth: formWidth }}>
            <FormLabel
              component="legend"
              required
              sx={{
                color: theme.palette.text.primary,
                ...theme.typography.body1,
              }}
            >
              Type of Operation
            </FormLabel>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.operationType.planned}
                  onChange={(e) => onFormDataChange(prev => ({
                    ...prev,
                    operationType: { ...prev.operationType, planned: e.target.checked }
                  }))}
                  sx={{
                    '&:not(.Mui-checked)': {
                      color: theme.palette.primary.dark,
                    }
                  }}
                />
              }
              label="Planned"
              sx={{ margin: 0, width: 'fit-content' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.operationType.emergency}
                  onChange={(e) => onFormDataChange(prev => ({
                    ...prev,
                    operationType: { ...prev.operationType, emergency: e.target.checked }
                  }))}
                  sx={{
                    '&:not(.Mui-checked)': {
                      color: theme.palette.primary.dark,
                    }
                  }}
                />
              }
              label="Emergency"
              sx={{ margin: 0, width: 'fit-content' }}
            />
          </FormGroup>
        </Stack>
      </Box>
    </Stack>
  );
}